import axios from 'axios';
import { createLogger } from '../../../utils/logger';
import baseUri from '../utils/base';

// Create a logger instance for the axios module
const logger = createLogger('AxiosInstance');

// Feature flag: control whether to proactively refresh on app/init mount
// Default: false (no preflight refresh; rely on 401-driven flow)
const INIT_REFRESH =
  String(process.env.REACT_APP_AXIOS_INIT_REFRESH || '')
    .toLowerCase()
    .trim() === 'true';

/**
 * A wrapper around Axios that handles authentication, token refresh, and request queuing.
 *
 * @class AxiosInstance
 * @description This class provides an enhanced Axios instance with built-in authentication handling,
 * automatic token refresh, request queuing, and retry mechanisms for API calls. It supports both
 * public and private (authenticated) API endpoints with secure HTTPS connections.
 *
 * @example
 * // Create a private API instance (requires authentication)
 * const api = new AxiosInstance({ isPrivate: true });
 *
 * // Make an authenticated request
 * const response = await api.request({ url: '/protected-resource', method: 'get' });
 *
 * // Register a listener for token refresh events
 * const unsubscribe = api.onTokenRefresh((authData) => {
 *   console.log('New token received:', authData.accessToken);
 * });
 *
 * @property {boolean} isPrivate - Whether this instance requires authentication
 * @property {object} instance - The underlying Axios instance
 * @property {string|null} authToken - The current authentication token
 * @property {boolean} hasValidToken - Whether we currently have a valid token
 * @property {boolean} refreshInProgress - Whether a token refresh is currently in progress
 * @property {number} refreshAttempts - The number of consecutive token refresh attempts
 * @property {number} maxRefreshAttempts - Maximum number of consecutive refresh attempts allowed
 * @property {string|null} lastKnownToken - The last known valid token (retained for auth endpoints)
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.7
 * @since 0.1.0-beta.7
 */
class AxiosInstance {
  /**
   * Determines if the current location is an auth-related route where we should not attempt
   * to auto-refresh tokens (e.g., login, MFA, lock screens, error pages).
   *
   * @returns {boolean}
   */
  isAuthRoute() {
    try {
      const path = (window?.location?.pathname || '').toLowerCase();
      return (
        path.startsWith('/auth/') ||
        path === '/login' ||
        path === '/lock' ||
        path.startsWith('/error/')
      );
    } catch (_) {
      return false;
    }
  }
  /**
   * Creates an instance of the Axios HTTP client.
   *
   * @constructor
   * @param {Object} options - Configuration options for the instance
   * @param {boolean} [options.isPrivate=true] - Whether this instance requires authentication
   * @param {Object} [options.httpsConfig={}] - HTTPS specific configuration options
   * @param {boolean} [options.httpsConfig.rejectUnauthorized=true] - Whether to reject self-signed certificates
   *
   * @property {boolean} isPrivate - Determines if authentication is required for requests
   * @property {string|null} authToken - The current authentication token
   * @property {boolean} hasValidToken - Flag indicating if the current token is valid
   * @property {boolean} refreshInProgress - Flag indicating if token refresh is in progress
   * @property {Promise|null} refreshTokenPromise - Promise for the current token refresh operation
   * @property {number} refreshAttempts - Count of token refresh attempts
   * @property {number} maxRefreshAttempts - Maximum number of allowed refresh attempts
   * @property {Array} listeners - Event listeners for authentication state changes
   * @property {Array} requestQueue - Queue for requests waiting for authentication
   * @property {boolean} tokenInitialized - Flag indicating if token has been initialized
   * @property {number|null} initialRefreshTimeoutId - Timeout ID for initial token refresh
   * @property {boolean} isInitializingApp - Flag indicating if app is in initialization phase
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @since 0.1.0-beta.7
   * @version 0.1.0-beta.7
   */
  constructor({ isPrivate = true, httpsConfig = {} } = {}) {
    this.isPrivate = isPrivate;
    this.httpsConfig = httpsConfig;
    this.isInitializingApp = true; // Flag to track app initialization state
    this.initInstance();

    // Authentication state
    this.authToken = null;
    this.hasValidToken = false;
    this.refreshInProgress = false;
    this.refreshTokenPromise = null;
    this.refreshAttempts = 0;
    this.maxRefreshAttempts = 3;
    this.listeners = [];
    this.refreshStartListeners = []; // Listeners for when refresh starts
    this.refreshFailureListeners = []; // Listeners for when refresh fails
    this.requestQueue = [];
    this.tokenInitialized = false;
    this.lastKnownToken = null;
    // Cache the last successful auth data for late listener registration
    this.lastAuthData = null;
    // Track per-request controllers for cancellation
    this.inflightControllers = new Set();
    // Track last visibility state for tab focus handling
    this.lastVisibilityHiddenAt = null;

    // Check if the base URI is using HTTPS
    this.isSecureConnection = baseUri.startsWith('https://');
    if (this.isSecureConnection) {
      logger.info('Using secure HTTPS connection for API communication');
    } else {
      logger.warn(
        'Using non-secure HTTP connection for API communication - not recommended for production'
      );
    }

    // Set a timeout to initialize token only if we appear to have a prior auth session
    if (this.isPrivate && INIT_REFRESH) {
      let shouldInit = false;
      try {
        // Heuristic: if a user id was persisted previously, we likely have a session cookie
        shouldInit = !!localStorage.getItem('auth_user_id');
        logger.debug(
          `Init refresh check: auth_user_id=${shouldInit ? 'exists' : 'missing'}, isAuthRoute=${this.isAuthRoute()}`
        );
      } catch (e) {
        // ignore storage access errors
      }

      // Do not attempt an init refresh while on auth/error routes
      if (shouldInit && !this.isAuthRoute()) {
        logger.info('Scheduling init refresh in 100ms');
        this.initialRefreshTimeoutId = setTimeout(() => {
          this.initializeToken();
        }, 100);
      } else if (!shouldInit) {
        logger.debug('Skipping init refresh: no auth_user_id in localStorage');
      }
    }
  }

  /**
   * Initializes the authentication token for private API access.
   * If the token is already initialized or the instance is not private, it returns immediately.
   * Otherwise, it attempts to refresh the token and handles any errors that occur.
   *
   * @async
   * @returns {Promise<boolean>} Returns true if token initialization succeeds or is not needed,
   *                            false if token refresh fails.
   * @throws {Error} Errors from token refresh are caught internally and logged.
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @version 0.1.0-beta.7
   * @since 0.1.0-beta.7
   */
  async initializeToken() {
    if (this.tokenInitialized || !this.isPrivate) return true;

    logger.info('Initializing authentication token');

    try {
      await this.refreshToken();
      return true;
    } catch (error) {
      logger.error('Failed to initialize token:', error);
      return false;
    } finally {
      // Mark as initialized regardless of outcome to avoid repeat attempts
      this.tokenInitialized = true;

      if (this.initialRefreshTimeoutId) {
        clearTimeout(this.initialRefreshTimeoutId);
        this.initialRefreshTimeoutId = null;
      }
    }
  }

  /**
   * Initializes the Axios instance with default configuration.
   *
   * This method:
   * - Creates a new AbortController for request cancellation
   * - Sets up the Axios instance with base URL, default headers, and withCredentials flag
   * - Configures request and response interceptors
   * - Applies HTTPS-specific settings for secure connections
   *
   * @returns {void}
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @version 0.1.0-beta.7
   * @since 0.1.0-beta.7
   */
  initInstance() {
    // Prepare axios config with HTTPS settings
    const axiosConfig = {
      baseURL: String(baseUri),
      headers: { 'Content-Type': 'application/json' },
      withCredentials: true,
      timeout: 30000 // 30 second timeout
    };

    // Apply HTTPS-specific configuration
    if (this.isSecureConnection) {
      // Configure HTTPS-specific settings
      const { rejectUnauthorized = true } = this.httpsConfig;

      // Add HTTPS agent configuration if needed
      if (rejectUnauthorized === false) {
        logger.warn(
          'SSL certificate verification disabled - this should only be used in development'
        );
        // In a browser environment, we can't directly configure this
        // This would typically be handled by the server config or a proxy in development
      }
    }

    this.instance = axios.create(axiosConfig);

    this.setupRequestInterceptor();
    this.setupResponseInterceptor();
  }

  /**
   * Determines if a given URL should bypass token initialization and queuing.
   * These endpoints must be callable without a valid access token to avoid deadlocks
   * in flows like unlocking a locked session or refreshing tokens.
   *
   * @param {string} url
   * @returns {boolean}
   */
  isBypassEndpoint(url) {
    if (!url) return false;
    // Normalize to ensure consistent checks
    const u = String(url);
    // Endpoints that must not be gated by token availability
    return (
      u.includes('/refresh') ||
      u.includes('/auth/unlock') ||
      u.includes('/auth/verify') || // MFA verify
      u.includes('/auth/login') ||
      u.includes('/auth/lock') || // allow lock call even if token was removed early
      u.includes('/auth/logout') // allow logout without forcing token init/refresh
    );
  }

  /**
   * Sets up request interceptors for the Axios instance.
   *
   * This method configures the request interceptor to:
   * 1. Add an abort controller signal to each request
   * 2. Record request timestamp
   * 3. Handle authentication token management:
   *    - Allow refresh token requests to proceed immediately
   *    - For private endpoints:
   *      - Queue requests when no valid token is available
   *      - Add Authorization header with Bearer token when available
   *
   * @returns {void}
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @version 0.1.0-beta.7
   * @since 0.1.0-beta.7
   */
  setupRequestInterceptor() {
    this.instance.interceptors.request.use(
      config => {
        // Respect an existing AbortSignal if provided by caller; otherwise create one
        if (!config.signal) {
          const controller = new AbortController();
          config.signal = controller.signal;
          // Keep a reference to clean up on completion
          // and enable cancelAllRequests to abort in-flight calls
          config._internalAbortController = controller;
          this.inflightControllers.add(controller);
        }

        config._requestTimestamp = Date.now();

        // For bypass endpoint requests, let them through immediately
        if (this.isBypassEndpoint(config.url)) {
          // Still attach Authorization if we have a current or last known token
          if (this.isPrivate) {
            const token = this.authToken || this.lastKnownToken;
            if (
              token &&
              (!config.headers || !config.headers['Authorization'])
            ) {
              config.headers = config.headers || {};
              config.headers['Authorization'] = `Bearer ${token}`;
            }
          }
          return config;
        }

        // For all other private requests, attach token if we have one; otherwise
        // let the request proceed without token to allow a 401 that will trigger refresh.
        // Only queue when a refresh is actively in progress to deduplicate.
        if (this.isPrivate) {
          if (!this.hasValidToken) {
            if (this.refreshInProgress || this.refreshTokenPromise) {
              logger.debug(
                `Queueing request while refresh in progress: ${config.url}`
              );
              return new Promise((resolve, reject) => {
                this.enqueueRequest({
                  resolve: () => resolve(config),
                  reject,
                  config,
                  timestamp: config._requestTimestamp
                });
              });
            }
            // Proceed without Authorization header; expect 401 -> refresh
          }

          // Make sure the request has the token when available
          if (
            (!config.headers || !config.headers['Authorization']) &&
            this.authToken
          ) {
            config.headers = config.headers || {};
            config.headers['Authorization'] = `Bearer ${this.authToken}`;
          }
        }

        return config;
      },
      error => Promise.reject(error)
    );
  }

  /**
   * Sets up an Axios response interceptor to handle request failures.
   *
   * The interceptor provides the following functionality:
   * - Passes successful responses through unchanged
   * - Handles cancelled requests
   * - Handles network errors or null responses
   * - Implements automatic token refresh for 401 (Unauthorized) errors:
   *   - Enqueues the failed request
   *   - Refreshes the access token if not already in progress
   *   - Retries the original request once the token is refreshed
   *   - Limits retry attempts based on maxRefreshAttempts
   * - Standardizes error responses with original status codes
   *
   * @returns {void}
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @version 0.1.0-beta.7
   * @since 0.1.0-beta.7
   */
  setupResponseInterceptor() {
    this.instance.interceptors.response.use(
      response => {
        // Cleanup per-request controller tracking after completion
        try {
          const cfg = response?.config;
          if (cfg?._internalAbortController) {
            this.inflightControllers.delete(cfg._internalAbortController);
            delete cfg._internalAbortController;
          }
        } catch (_) {
          // no-op cleanup
        }
        return response;
      },
      async error => {
        const originalConfig = error.config;

        // Cleanup inflight controller tracking on error as well
        try {
          if (originalConfig?._internalAbortController) {
            this.inflightControllers.delete(
              originalConfig._internalAbortController
            );
            delete originalConfig._internalAbortController;
          }
        } catch (_) {
          // no-op cleanup
        }

        // If the request was cancelled, just reject
        if (axios.isCancel(error)) {
          return Promise.reject(error);
        }

        // For network errors or null response, reject immediately
        if (!error.response) {
          return Promise.reject(error);
        }

        // Handle MFA gating explicitly: if backend indicates MFA is required or setup required,
        // do not attempt token refresh; instead redirect user to the appropriate MFA screen.
        if (this.isPrivate && error.response.status === 401) {
          const apiErrorName =
            error.response?.data?.name || error.response?.data?.error?.name;

          if (
            apiErrorName === 'MFAVerificationRequired' ||
            apiErrorName === 'MFASetupRequired'
          ) {
            try {
              // Preserve current path for redirect after MFA completion
              const attemptedUrl =
                window.location.pathname + window.location.search;
              try {
                localStorage.setItem('redirectUri', attemptedUrl);
              } catch (e) {
                // ignore storage errors
              }

              // Navigate to correct MFA route
              const mfaRoute =
                apiErrorName === 'MFAVerificationRequired'
                  ? '/auth/otp'
                  : '/auth/mfa';

              if (!this.isBypassEndpoint(originalConfig?.url)) {
                // prevent retry loops and just redirect
                window.location.assign(mfaRoute);
              }
            } catch (_) {
              // swallow navigation errors
            }
            // Reject with original error
            return Promise.reject({
              ...error.response.data,
              statusCode: error.response.status
            });
          }
        }

        // Handle 401 errors by refreshing the token and retrying the request
        if (
          this.isPrivate &&
          error.response.status === 401 &&
          !originalConfig._retry &&
          !this.isBypassEndpoint(originalConfig.url)
        ) {
          // If we're on an auth route (login/MFA/etc.), don't attempt refresh; just reject.
          if (this.isAuthRoute()) {
            return Promise.reject({
              ...error.response.data,
              statusCode: error.response.status
            });
          }
          // Mark the request as retried
          originalConfig._retry = true;

          // Check if we've exceeded retry attempts
          if (this.refreshAttempts >= this.maxRefreshAttempts) {
            logger.warn(
              `Maximum refresh attempts (${this.maxRefreshAttempts}) reached`
            );
            return Promise.reject(error);
          }

          try {
            // Queue the failed request
            const responsePromise = new Promise((resolve, reject) => {
              this.enqueueRequest({
                resolve,
                reject,
                config: originalConfig,
                timestamp: originalConfig._requestTimestamp || Date.now()
              });
            });

            // Only start a refresh if one isn't already in progress
            if (!this.refreshInProgress) {
              // Refresh token first
              await this.refreshToken();
            }

            // The queued request will be processed when refresh completes
            return responsePromise;
          } catch (refreshError) {
            logger.error('Token refresh failed:', refreshError);
            return Promise.reject(error);
          }
        }

        // Return error with original status code
        return Promise.reject({
          ...error.response.data,
          statusCode: error.response.status
        });
      }
    );
  }

  /**
   * Adds a request to the request queue.
   * @param {Object} request - The request object to be queued.
   * @param {Object} request.config - The configuration object of the request.
   * @param {string} request.config.url - The URL of the request.
   * @returns {void}
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @version 0.1.0-beta.7
   * @since 0.1.0-beta.7
   */
  enqueueRequest(request) {
    logger.debug(`Queuing request to: ${request.config.url}`);

    // Preserve and listen to the request's AbortSignal to support cancellation while queued
    const cfg = request.config || {};
    const signal = cfg.signal;
    let abortListener = null;
    request._canceled = false;
    request._signal = signal;

    if (signal && typeof signal.addEventListener === 'function') {
      abortListener = () => {
        request._canceled = true;
        try {
          // Reject with a cancellation-like error shape that axios.isCancel can recognize
          const err = new Error('Request cancelled');
          err.name = 'CanceledError';
          err.code = 'ERR_CANCELED';
          request.reject(err);
        } catch (_) {
          // ignore
        }
      };
      signal.addEventListener('abort', abortListener, { once: true });
      request._removeAbortListener = () => {
        try {
          signal.removeEventListener('abort', abortListener);
        } catch (_) {
          // ignore
        }
      };
    }

    this.requestQueue.push(request);
  }

  /**
   * Processes the queued requests that were intercepted during token refresh.
   *
   * This method handles all queued requests by either:
   * 1. Rejecting them all with an error if one is provided
   * 2. Processing them with the new auth token
   *
   * For initialization requests, it resolves with the config object.
   * For standard requests, it executes the request with the instance and resolves/rejects accordingly.
   *
   * @param {Error|null} error - If provided, all queued requests will be rejected with this error
   * @param {string|null} token - The new authentication token to use for the queued requests
   * @returns {void}
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @version 0.1.0-beta.7
   * @since 0.1.0-beta.7
   */
  processRequestQueue(error = null, token = null) {
    const queueCopy = [...this.requestQueue];
    this.requestQueue = [];

    logger.debug(`Processing ${queueCopy.length} queued requests`);

    if (error) {
      queueCopy.forEach(request => {
        request.reject(error);
      });
      return;
    }

    // Process requests with the token
    queueCopy.forEach(request => {
      try {
        const config = { ...request.config };

        // Detach abort listener if present
        if (request._removeAbortListener) {
          request._removeAbortListener();
          delete request._removeAbortListener;
        }

        // If request was canceled while queued, reject immediately
        if (request._canceled || request._signal?.aborted) {
          const err = new Error('Request cancelled');
          err.name = 'CanceledError';
          err.code = 'ERR_CANCELED';
          request.reject(err);
          return;
        }

        // Add token to headers if available
        if (token) {
          config.headers = config.headers || {};
          config.headers['Authorization'] = `Bearer ${token}`;
        }

        // For initialization requests, resolve the config
        if (config._isInitializationRequest) {
          request.resolve(config);
          return;
        }

        // Execute the request
        this.instance(config)
          .then(response => request.resolve(response))
          .catch(error => request.reject(error));
      } catch (err) {
        request.reject(err);
      }
    });
  }

  /**
   * Refreshes the authentication token.
   *
   * This method attempts to refresh the authentication token by making a direct request to the
   * refresh endpoint. It includes handling for concurrent refresh attempts (returning the same
   * promise), request timeouts, and tracking refresh attempts.
   *
   * - If a refresh is already in progress, returns the existing promise
   * - Implements a 10-second timeout for the refresh request
   * - Updates internal token state upon success
   * - Notifies registered listeners with updated authentication data
   * - Processes any queued requests after refresh completion
   * - Tracks refresh attempts and limits retries
   *
   * @async
   * @returns {Promise<string>} A promise that resolves to the refreshed access token
   * @throws {Error} If the refresh request fails or returns invalid data
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @version 0.1.0-beta.7
   * @since 0.1.0-beta.7
   */
  async refreshToken() {
    // Never attempt refresh while on auth/login routes
    if (this.isAuthRoute()) {
      const err = new Error('Skip refresh on auth route');
      err.code = 'REFRESH_SKIPPED_ON_AUTH_ROUTE';
      return Promise.reject(err);
    }
    // If we already have a refresh in progress, return that promise
    if (this.refreshTokenPromise) {
      return this.refreshTokenPromise;
    }

    this.refreshInProgress = true;
    this.refreshAttempts++;

    // Notify listeners that refresh has started so React can update state
    this.notifyRefreshStartListeners();

    logger.debug(
      `Refreshing token (Attempt ${this.refreshAttempts}/${this.maxRefreshAttempts})`
    );

    // Create a new refresh promise
    this.refreshTokenPromise = (async () => {
      try {
        const controller = new AbortController();
        // Reduce timeout to avoid long hangs when no session exists or network issues occur
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        // Direct axios call for token refresh using the secure baseUri
        const response = await axios({
          method: 'post',
          url: `${baseUri}/v1/account/auth/refresh`,
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
            'Cache-Control': 'no-cache, no-store',
            Pragma: 'no-cache'
          },
          withCredentials: true,
          signal: controller.signal,
          timeout: 5000
        });

        clearTimeout(timeoutId);

        // Validate response
        if (!response?.data?.data?.authentication?.accessToken) {
          throw new Error('Invalid response from refresh endpoint');
        }

        const authData = response.data.data.authentication;
        const accountData = response.data.data.account;
        const token = authData.accessToken;

        // Update token state
        this.setAuthToken(token);
        this.tokenInitialized = true;
        this.refreshAttempts = 0;

        // Mark initialization as complete after first successful token refresh
        if (this.isInitializingApp) {
          this.isInitializingApp = false;
          logger.info('Application initialization completed');
        }

        // Notify listeners with the full auth data
        this.notifyListeners({
          ...authData,
          account: accountData
        });

        // Persist a minimal session marker to enable safe rehydration checks on reload
        try {
          const markerId = accountData?.id || authData?.id;
          if (markerId) localStorage.setItem('auth_user_id', String(markerId));
        } catch (_) {
          // ignore storage errors
        }

        // Process any queued requests
        this.processRequestQueue(null, token);

        logger.debug('Token refresh completed successfully');
        return token;
      } catch (error) {
        logger.error('Token refresh failed:', error);

        // Notify listeners that refresh failed so React can clear refreshing state
        this.notifyRefreshFailureListeners(error);

        // Process queued requests with error
        this.processRequestQueue(error);
        throw error;
      } finally {
        this.refreshInProgress = false;
        this.refreshTokenPromise = null;
      }
    })();

    return this.refreshTokenPromise;
  }

  /**
   * Forces a token refresh regardless of the token's expiration status.
   *
   * This method attempts to refresh the authentication token and returns the result of the refresh operation.
   * If the refresh fails, it logs the error and returns null.
   *
   * @async
   * @returns {Promise<Object|null>} The result of the token refresh operation, or null if the refresh failed.
   * @throws {Error} Errors are caught internally and logged, but not propagated.
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @version 0.1.0-beta.7
   * @since 0.1.0-beta.7
   */
  async forceRefresh() {
    try {
      return await this.refreshToken();
    } catch (error) {
      logger.error('Force refresh failed:', error);
      return null;
    }
  }

  /**
   * Sets the authentication token for this instance and updates the headers.
   * If the instance is marked as private and a token is provided, it will:
   * 1. Store the token internally
   * 2. Mark the instance as having a valid token
   * 3. Update the Authorization header with the Bearer token
   * 4. Process any requests that were queued while waiting for authentication
   *
   * @param {string} token - The JWT or authentication token to use for requests
   * @returns {void}
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @version 0.1.0-beta.7
   * @since 0.1.0-beta.7
   */
  setAuthToken(token) {
    if (this.isPrivate && token) {
      this.authToken = token;
      this.hasValidToken = true;
      this.lastKnownToken = token;
      this.instance.defaults.headers.common['Authorization'] =
        `Bearer ${token}`;
      logger.debug('Auth token set successfully');

      // Process any queued requests immediately
      if (this.requestQueue.length > 0) {
        this.processRequestQueue(null, token);
      }
    }
  }

  /**
   * Removes the authentication token from the axios instance headers.
   * This method will only work if the instance is marked as private.
   * After removing the token, it sets the authToken to null and
   * hasValidToken flag to false.
   *
   * @returns {void}
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @version 0.1.0-beta.7
   * @since 0.1.0-beta.7
   */
  removeAuthToken() {
    if (this.isPrivate) {
      delete this.instance.defaults.headers.common['Authorization'];
      this.authToken = null;
      this.hasValidToken = false;
      // Clear cached auth data to prevent stale data on re-authentication
      this.lastAuthData = null;
      // Intentionally retain lastKnownToken so that bypass endpoints (e.g., unlock)
      // can authenticate even while the session is in a locked state.
      logger.debug('Auth token removed');
    }
  }

  /**
   * Registers a callback to be invoked when a token refresh occurs.
   * If a token refresh already completed before this listener was registered,
   * the callback is invoked immediately with the cached auth data.
   *
   * @param {Function} callback - The function to be called when a token refresh happens
   * @returns {Function} A cleanup function that, when called, removes the registered callback
   * @example
   * const cleanup = axiosInstance.onTokenRefresh(() => {
   *   console.log('Token has been refreshed');
   * });
   *
   * // Later when you want to stop listening
   * cleanup();
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @version 0.1.0-beta.7
   * @since 0.1.0-beta.7
   */
  onTokenRefresh(callback) {
    this.listeners.push(callback);
    logger.debug(
      `Token refresh listener registered. Total listeners: ${this.listeners.length}, lastAuthData: ${this.lastAuthData ? 'present' : 'null'}, tokenInitialized: ${this.tokenInitialized}`
    );

    // If we already have cached auth data from a completed refresh,
    // immediately notify this late-registering listener
    if (this.lastAuthData && this.tokenInitialized) {
      logger.debug(
        'Late listener registered, immediately invoking with cached auth data'
      );
      try {
        callback(this.lastAuthData);
      } catch (err) {
        logger.error('Error in late token refresh listener:', err);
      }
    }

    return () => {
      this.listeners = this.listeners.filter(cb => cb !== callback);
    };
  }
  /**
   * Notifies all registered listeners about token refresh events.
   * Calls each listener callback with the authentication data and handles any errors
   * that might occur during the callback execution.
   * Also caches the auth data so late-registering listeners can receive it.
   *
   * @param {Object} authData - The authentication data to pass to listeners
   * @returns {void}
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @version 0.1.0-beta.7
   * @since 0.1.0-beta.7
   */
  notifyListeners(authData) {
    // Cache the auth data for late-registering listeners
    this.lastAuthData = authData;

    logger.debug(`Notifying ${this.listeners.length} token refresh listeners`);

    for (const callback of this.listeners) {
      try {
        callback(authData);
      } catch (err) {
        logger.error('Error in token refresh listener:', err);
      }
    }
  }

  /**
   * Public method to notify listeners programmatically.
   * This is used by the AuthenticationProvider to trigger token refresh notifications.
   *
   * @param {Object} authData - The authentication data to pass to listeners
   * @returns {void}
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @version 0.1.0-beta.7
   * @since 0.1.0-beta.7
   */
  notifyOnTokenRefresh(authData) {
    this.notifyListeners(authData);
  }

  /**
   * Registers a callback to be invoked when a token refresh starts.
   * This allows React components to track when refresh is in progress.
   *
   * @param {Function} callback - The function to be called when a token refresh starts
   * @returns {Function} A cleanup function that removes the registered callback
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @version 0.1.0-beta.8
   * @since 0.1.0-beta.8
   */
  onRefreshStart(callback) {
    this.refreshStartListeners.push(callback);
    return () => {
      this.refreshStartListeners = this.refreshStartListeners.filter(
        cb => cb !== callback
      );
    };
  }

  /**
   * Notifies all registered listeners that a token refresh has started.
   *
   * @returns {void}
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @version 0.1.0-beta.8
   * @since 0.1.0-beta.8
   */
  notifyRefreshStartListeners() {
    logger.debug(
      `Notifying ${this.refreshStartListeners.length} refresh start listeners`
    );

    for (const callback of this.refreshStartListeners) {
      try {
        callback();
      } catch (err) {
        logger.error('Error in refresh start listener:', err);
      }
    }
  }

  /**
   * Registers a callback to be invoked when a token refresh fails.
   * This allows React components to clear the refreshing state on failure.
   *
   * @param {Function} callback - The function to be called when a token refresh fails
   * @returns {Function} A cleanup function that removes the registered callback
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @version 0.1.0-beta.8
   * @since 0.1.0-beta.8
   */
  onRefreshFailure(callback) {
    this.refreshFailureListeners.push(callback);
    return () => {
      this.refreshFailureListeners = this.refreshFailureListeners.filter(
        cb => cb !== callback
      );
    };
  }

  /**
   * Notifies all registered listeners that a token refresh has failed.
   *
   * @param {Error} error - The error that caused the refresh to fail
   * @returns {void}
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @version 0.1.0-beta.8
   * @since 0.1.0-beta.8
   */
  notifyRefreshFailureListeners(error) {
    logger.debug(
      `Notifying ${this.refreshFailureListeners.length} refresh failure listeners`
    );

    for (const callback of this.refreshFailureListeners) {
      try {
        callback(error);
      } catch (err) {
        logger.error('Error in refresh failure listener:', err);
      }
    }
  }

  /**
   * Sends an HTTP request using the configured axios instance.
   * For private API endpoints, automatically ensures a valid authentication token is available before making the request.
   *
   * @async
   * @param {Object} config - The axios request configuration
   * @param {string} [config.url] - The URL for the request
   * @returns {Promise<Object|null>} - Returns the axios response object if successful, or null if request was cancelled
   * @throws {Error} - Rethrows any errors that occur during the request (except for cancellation)
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @version 0.1.0-beta.7
   * @since 0.1.0-beta.7
   */
  async request(config) {
    try {
      // For private API requests, optionally perform an init-refresh based on feature flag.
      // Default behavior is disabled; rely on 401-driven refresh.
      if (
        INIT_REFRESH &&
        this.isPrivate &&
        !this.isBypassEndpoint(config.url)
      ) {
        await this.initializeToken();
      }

      const response = await this.instance.request(config);
      return response;
    } catch (error) {
      if (axios.isCancel(error)) {
        logger.warn('Request cancelled');
        return null;
      }
      throw error;
    }
  }

  /**
   * Cancels all pending requests made through this instance.
   * This method:
   * 1. Aborts ongoing requests using the AbortController
   * 2. Reinitializes the API instance
   * 3. Restores the authentication token if previously set
   *
   * Any error during the cancellation process is logged but not thrown.
   *
   * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
   * @version 0.1.0-beta.7
   * @since 0.1.0-beta.7
   */
  cancelAllRequests() {
    try {
      logger.debug('Cancelling all pending and queued requests');

      // Abort all in-flight requests created without a caller-provided signal
      for (const controller of Array.from(this.inflightControllers)) {
        try {
          controller.abort();
        } catch (_) {
          // ignore
        }
        this.inflightControllers.delete(controller);
      }

      // Reject and clear queued requests
      if (this.requestQueue.length > 0) {
        const queued = [...this.requestQueue];
        this.requestQueue = [];
        queued.forEach(req => {
          try {
            if (req._removeAbortListener) req._removeAbortListener();
            const err = new Error('Request cancelled');
            err.name = 'CanceledError';
            err.code = 'ERR_CANCELED';
            req.reject(err);
          } catch (_) {
            // ignore
          }
        });
      }
    } catch (error) {
      logger.error('Error cancelling requests:', error);
    }
  }
}

// Create singleton instances
const axiosPublic = new AxiosInstance({ isPrivate: false });
const axiosPrivate = new AxiosInstance({ isPrivate: true });

/**
 * Make a request with the provided axios instance
 * @param {AxiosInstance} axiosInstance - The axios instance to use
 * @param {Object} config - Request configuration
 * @param {AbortSignal} abortSignal - Optional abort signal
 * @returns {Promise<Object|null>} Response data or null if cancelled
 *
 * @author Brandon Cummings <brandon.cummings@leargassecurity.com>
 * @version 0.1.0-beta.7
 * @since 0.1.0-beta.7
 */
const makeRequest = async (axiosInstance, config, abortSignal = null) => {
  try {
    if (abortSignal) {
      config.signal = abortSignal;
    }
    const response = await axiosInstance.request(config);
    return response.data;
  } catch (error) {
    if (axios.isCancel(error)) {
      return null;
    }
    throw error;
  }
};

export { axiosPrivate, axiosPublic, makeRequest };
